import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import useModals from 'src/Hooks/useModals'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { useDevicePairing } from '../../hooks'
import { getDeviceDefaultName, removeEvseExpandedFromCache, setIsEvseExpandedToCache } from 'src/Libs/flow/parsers'
import { deleteIndividualDevice } from 'src/Libs/flow/installation/api/save-installation/get-installation-delete-requests'
import { checkTrueRecursively } from 'src/Libs/flow/utils'
import { Device } from 'src/Libs/flow/installation/forms'
import { useFormState, useWatch } from 'react-hook-form'

export type EvseType = 'eebus' | 'modbusTcp'

interface UseEvseEditorProps {
  evseType: EvseType
  device: Device
  deviceCategoryName: 'EEBus Evse' | 'ModbusTCP Evse'
  index: number
}

interface UseEvseEditorReturnType {
  isDirty: boolean
  isError: boolean
  isExpanded: boolean
  toggleExpanded: () => void
  canPair: boolean
  handlePairing: () => void
  displayName: string
  handleDelete: () => void
}

export const useEvseEditor = ({ evseType, device, deviceCategoryName, index }: UseEvseEditorProps): UseEvseEditorReturnType => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()

  const { openConfirmationModal, openInstallationDeleteDialog } = useModals()

  const { control, getValues, setValue, reset } = useInstallationForm()
  const formState = useFormState({ control, name: `chargingStations.${evseType}.${index}` })
  const isDirty = checkTrueRecursively(formState.dirtyFields.chargingStations?.[evseType]?.[index])
  const isError = !!Object.keys(formState.errors.chargingStations?.[evseType]?.[index] ?? {}).length
  const isExpanded = !!useWatch({ control, name: `chargingStationsExpandedState.${evseType}.${device.id}` })

  const { canPair, handlePairing } = useDevicePairing(device)
  const displayName = getDeviceDefaultName(device.name, deviceCategoryName, device.deviceId, t)

  const toggleExpanded = (): void => {
    const newValue = !isExpanded
    if (!device.isNew) setIsEvseExpandedToCache(device.deviceId, newValue)
    setValue(`chargingStationsExpandedState.${evseType}.${device.id}`, newValue)
  }

  const handleDelete = (): void => {
    openConfirmationModal({
      title: t('installation.deleteConfirmation.title'),
      text: `${t('installation.deleteConfirmation.selectedDevices')} ${displayName}`,
      buttonText: [t('common.cancel'), t('common.confirm')],
      onConfirm: async onClose => {
        const deviceToDelete = getValues(`chargingStations.${evseType}.${index}`) as Device
        removeEvseExpandedFromCache(deviceToDelete.deviceId)
        Promise.resolve().then(() => {
          deleteIndividualDevice(deviceToDelete, getValues, setValue, openInstallationDeleteDialog, reset, t, authFetch)
        })
        onClose()
      },
    })
  }

  return { isDirty, isError, isExpanded, toggleExpanded, canPair, handlePairing, displayName, handleDelete }
}
